<template>
	<!--
		Setas ◀ ▶ só na faixa das abas (Delphi PageControl).
		Rolam os títulos quando não cabem na largura do coletor.
	-->
	<div ref="rootEl" class="r3-tabpanel-root flex flex-column w-full h-full min-h-0">
		<button
			type="button"
			class="r3-tab-arrow r3-tab-arrow-left"
			aria-label="Rolar abas para a esquerda"
			:disabled="!canScrollLeft"
			@click="rolarAbas(-1)"
		>
			<span class="pi pi-chevron-left" />
		</button>
		<button
			type="button"
			class="r3-tab-arrow r3-tab-arrow-right"
			aria-label="Rolar abas para a direita"
			:disabled="!canScrollRight"
			@click="rolarAbas(1)"
		>
			<span class="pi pi-chevron-right" />
		</button>

		<TabView
			ref="tabViewRef"
			:active-index="tabPanel.activeIndex.value"
			class="r3-tabview flex flex-column flex-1 min-h-0 w-full h-full"
			@tab-change="emitTabChange"
		>
			<TabPanel
				v-for="item in tabPanel.visiblePanels.value"
				:key="item.name"
				:header="item.header"
				:pt="{ content: 'h-full p-0' }"
			>
				<slot :name="item.name" />
			</TabPanel>
		</TabView>
	</div>
</template>

<script setup lang="ts">
	import { R3TabPanelClass } from "~/classes/base/R3TabPanel.classes";

	const tabPanel = new R3TabPanelClass();

	const props = withDefaults(
		defineProps<{
			panels: Array<{ name: string; header: string; visible: boolean }>;
		}>(),
		{},
	);

	const activeIndex = defineModel<number>("activeIndex", { default: 0 });

	watch(
		() => activeIndex.value,
		(value) => {
			tabPanel.activeIndex.value = value;
		},
		{ immediate: true },
	);

	const emits = defineEmits(["tab-change"]);

	watch(
		() => tabPanel.activeIndex.value,
		(value) => {
			if (value !== activeIndex.value) {
				activeIndex.value = value;
			}
		},
		{ immediate: true },
	);

	defineSlots<{
		[K in (typeof props.panels)[number]["name"]]: () => any;
	}>();

	function emitTabChange(params: { index: number; originalEvent: Event }) {
		emits("tab-change", params);
		activeIndex.value = params.index;
	}

	type TabPanelItem = { name: string; header: string; visible?: boolean };

	function toPanelsArray(lista: unknown): TabPanelItem[] {
		if (Array.isArray(lista)) {
			return lista as TabPanelItem[];
		}
		if (lista != null && typeof lista === "object") {
			const maybeRef = lista as { value?: unknown };
			if (Array.isArray(maybeRef.value)) {
				return maybeRef.value as TabPanelItem[];
			}
			const like = lista as { length?: number; filter?: unknown };
			if (typeof like.filter === "function" && typeof like.length === "number") {
				try {
					return Array.from(lista as ArrayLike<TabPanelItem>);
				}
				catch {
					/* fallthrough */
				}
			}
		}
		return [];
	}

	function syncPanelsFromProps(lista: unknown) {
		const panels = toPanelsArray(lista).map(p => ({
			name: p.name,
			header: p.header,
			visible: p.visible !== false,
		}));
		tabPanel.panels.value = panels;
		tabPanel.visiblePanels.value = panels.filter(i => i.visible === true);
	}

	const rootEl = ref<HTMLElement | null>(null);
	const tabViewRef = ref<{ $el?: HTMLElement } | null>(null);
	const canScrollLeft = ref(false);
	const canScrollRight = ref(false);
	let navScrollEl: HTMLElement | null = null;
	let navObserver: ResizeObserver | null = null;

	function obterNavScroll(): HTMLElement | null {
		const root
			= (tabViewRef.value as { $el?: HTMLElement } | null)?.$el
				?? (rootEl.value?.querySelector(".p-tabview") as HTMLElement | null);
		if (!root) return null;
		return (
			(root.querySelector(".p-tabview-nav-content") as HTMLElement | null)
			?? (root.querySelector(".p-tabview-nav-container") as HTMLElement | null)
			?? (root.querySelector("ul.p-tabview-nav") as HTMLElement | null)
		);
	}

	function atualizarSetas() {
		const el = obterNavScroll() ?? navScrollEl;
		navScrollEl = el;
		if (!el) {
			canScrollLeft.value = false;
			canScrollRight.value = false;
			return;
		}
		el.style.overflowX = "auto";
		el.style.overflowY = "hidden";
		const max = el.scrollWidth - el.clientWidth;
		canScrollLeft.value = el.scrollLeft > 2;
		canScrollRight.value = max > 2 && el.scrollLeft < max - 2;
	}

	function rolarAbas(direcao: number) {
		const el = obterNavScroll() ?? navScrollEl;
		if (!el) return;
		const passo = Math.max(100, Math.floor(el.clientWidth * 0.55));
		el.scrollBy({ left: direcao * passo, behavior: "smooth" });
		window.setTimeout(atualizarSetas, 220);
	}

	function anexarListenersNav() {
		const el = obterNavScroll();
		if (!el) return;
		if (navScrollEl && navScrollEl !== el) {
			navScrollEl.removeEventListener("scroll", atualizarSetas);
		}
		navScrollEl = el;
		el.addEventListener("scroll", atualizarSetas, { passive: true });
		atualizarSetas();
	}

	onMounted(() => {
		syncPanelsFromProps(props.panels);
		tabPanel.activeIndex.value = activeIndex.value;
		nextTick(() => {
			anexarListenersNav();
			if (typeof ResizeObserver !== "undefined") {
				navObserver = new ResizeObserver(() => atualizarSetas());
				const el = obterNavScroll();
				if (el) navObserver.observe(el);
				if (rootEl.value) navObserver.observe(rootEl.value);
			}
			window.addEventListener("resize", atualizarSetas);
		});
	});

	onBeforeUnmount(() => {
		navScrollEl?.removeEventListener("scroll", atualizarSetas);
		navObserver?.disconnect();
		navObserver = null;
		window.removeEventListener("resize", atualizarSetas);
	});

	watch(
		() => props.panels,
		(lista) => {
			syncPanelsFromProps(lista);
			nextTick(() => {
				anexarListenersNav();
				atualizarSetas();
			});
		},
		{ deep: true, immediate: true },
	);

	watch(
		tabPanel.panels,
		(value) => {
			const panels = toPanelsArray(value);
			tabPanel.visiblePanels.value = panels.filter(i => i.visible !== false);
			nextTick(() => {
				anexarListenersNav();
				atualizarSetas();
			});
		},
		{ deep: true },
	);

	watch(activeIndex, () => {
		nextTick(() => {
			const el = obterNavScroll();
			const active = el?.querySelector(
				".p-highlight, .p-tabview-selected, li.p-highlight",
			) as HTMLElement | null;
			active?.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
			atualizarSetas();
		});
	});

	type ExposeType = {
		panels: typeof props.panels;
	} & typeof tabPanel;

	const expose = {
		panels: props.panels,
	} as ExposeType;

	useExposeProperties(expose, tabPanel);

	defineExpose(expose);
</script>

<style lang="scss" scoped>
	.r3-tabpanel-root {
		position: relative;
		min-height: 0;
	}

	/* Setas só na altura da faixa de abas (Delphi) */
	.r3-tab-arrow {
		position: absolute;
		top: 0;
		z-index: 5;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 2rem;
		height: 2.75rem;
		border: 0;
		border-bottom: 1px solid var(--surface-border, #dee2e6);
		background: var(--surface-0, #fff);
		color: var(--primary-color, #2196f3);
		cursor: pointer;
		padding: 0;
		box-shadow: 0 0 4px rgba(0, 0, 0, 0.08);
	}

	.r3-tab-arrow-left {
		left: 0;
	}

	.r3-tab-arrow-right {
		right: 0;
	}

	.r3-tab-arrow:disabled {
		opacity: 0.35;
		cursor: default;
	}

	.r3-tab-arrow:not(:disabled):active {
		background: var(--surface-100, #e9ecef);
	}

	.r3-tabview {
		min-height: 0;
	}

	/* Espaço para as setas não cobrirem o 1º/último título */
	.r3-tabview :deep(.p-tabview-nav-container),
	.r3-tabview :deep(.p-tabview-nav-content) {
		padding-left: 2rem;
		padding-right: 2rem;
		overflow-x: auto !important;
		overflow-y: hidden !important;
		scrollbar-width: none;
		-ms-overflow-style: none;
	}

	.r3-tabview :deep(.p-tabview-nav-container::-webkit-scrollbar),
	.r3-tabview :deep(.p-tabview-nav-content::-webkit-scrollbar) {
		display: none;
		height: 0;
	}

	.r3-tabview :deep(ul.p-tabview-nav) {
		flex-wrap: nowrap !important;
		white-space: nowrap;
	}

	.r3-tabview :deep(ul.p-tabview-nav li) {
		flex: 0 0 auto;
	}

	.r3-tabview :deep(.p-tabview-panels) {
		flex: 1 1 auto;
		min-height: 0;
		overflow: auto;
	}

	.r3-tabview :deep(.p-tabview-panel) {
		min-height: 0;
		height: 100%;
	}
</style>
